import { apiRequest } from "@/lib/api/http";
import { logCrud } from "@/lib/activity/emit-store";
import { costBoolean, costNumber, costText } from "@/lib/api/cost-fields";
import type { CostCategory } from "@/types/fleet";

interface RemoteCostCategory {
  id: string;
  name?: string;
  code?: string;
  color?: string | null;
  is_system?: string;
  sort_order?: string;
}

interface ListResponse<T> {
  success: true;
  data: T[];
}

function mapCostCategory(category: RemoteCostCategory): CostCategory {
  return {
    id: category.id,
    name: costText(category.name),
    code: costText(category.code),
    color: costText(category.color),
    isSystem: costBoolean(category.is_system),
    sortOrder: costNumber(category.sort_order),
  };
}

export interface CostCategoryInput {
  name: string;
  code?: string;
  color?: string;
  sortOrder?: number;
}

export async function listCostCategories(): Promise<CostCategory[]> {
  const response = await apiRequest<ListResponse<RemoteCostCategory>>(
    "/api/cost-categories",
    { method: "GET", cache: "no-store" },
  );
  return response.data.map(mapCostCategory);
}

export async function createCostCategory(input: CostCategoryInput): Promise<void> {
  const response = await apiRequest<{ success: true; id: string }>("/api/cost-categories", {
    method: "POST",
    body: JSON.stringify({
      name: input.name,
      ...(input.code !== undefined ? { code: input.code } : {}),
      ...(input.color !== undefined ? { color: input.color } : {}),
      ...(input.sortOrder !== undefined ? { sort_order: String(input.sortOrder) } : {}),
    }),
  });
  logCrud("create", "cost_category", input.name, response.id);
}

export async function updateCostCategory(
  id: string,
  input: Partial<CostCategoryInput>,
): Promise<void> {
  await apiRequest<{ success: true }>(`/api/cost-categories/${id}`, {
    method: "PUT",
    body: JSON.stringify({
      ...(input.name !== undefined ? { name: input.name } : {}),
      ...(input.code !== undefined ? { code: input.code } : {}),
      ...(input.color !== undefined ? { color: input.color } : {}),
      ...(input.sortOrder !== undefined ? { sort_order: String(input.sortOrder) } : {}),
    }),
  });
  logCrud("update", "cost_category", input.name ?? "", id);
}

export async function deleteCostCategory(id: string): Promise<void> {
  await apiRequest<{ success: true }>(`/api/cost-categories/${id}`, {
    method: "DELETE",
  });
  logCrud("delete", "cost_category", "", id);
}
